import React, { useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";

/**
 * Scoped content-protection wrapper (deterrents only — true screenshot blocking
 * is not possible on the web). Active only while mounted, so it affects only the
 * page it wraps. Applies:
 *  - disable right-click, text copy/cut, and image drag/save
 *  - block Ctrl/⌘+P (print) and clear clipboard on PrintScreen
 *  - blur the content when the tab loses focus or a print is attempted
 */
export default function ContentProtection({ children }) {
  const [obscured, setObscured] = useState(false);

  const wmLabel = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("ranker-auth") || "{}").user || {};
      return (u.email || u.phone || u.name || "CLARIAS").toString();
    } catch (_) {
      return "CLARIAS";
    }
  })();
  const wmBg = (() => {
    const safe = wmLabel.replace(/[<>&]/g, "");
    const svg =
      `<svg xmlns='http://www.w3.org/2000/svg' width='320' height='170'>` +
      `<text x='20' y='90' transform='rotate(-28 160 85)' font-size='15' fill='rgba(30,30,30,0.08)' font-family='sans-serif'>${safe} · CLARIAS</text></svg>`;
    return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
  })();

  useEffect(() => {
    const prevent = (e) => { e.preventDefault(); return false; };
    const isPrintScreen = (e) =>
      e.key === "PrintScreen" || (e.key || "").toLowerCase() === "printscreen" ||
      e.code === "PrintScreen" || e.keyCode === 44;
    const handlePrintScreen = () => {
      const clear = () => {
        try {
          const p = navigator.clipboard && navigator.clipboard.writeText(" ");
          if (p && typeof p.catch === "function") p.catch(() => {});
        } catch (_) {}
      };
      clear();
      setTimeout(clear, 60);
      setTimeout(clear, 200);
      setObscured(true);
      setTimeout(() => setObscured(false), 1500);
    };
    const onKeyDown = (e) => {
      const k = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && k === "p") { e.preventDefault(); return false; }
      if (isPrintScreen(e)) { e.preventDefault(); handlePrintScreen(); }
    };
    const onKeyUp = (e) => { if (isPrintScreen(e)) handlePrintScreen(); };
    const onVisibility = () => setObscured(document.hidden);
    const onBlur = () => setObscured(true);
    const onFocus = () => setObscured(false);
    const onBeforePrint = () => setObscured(true);
    const onAfterPrint = () => setObscured(false);

    document.addEventListener("contextmenu", prevent);
    document.addEventListener("copy", prevent);
    document.addEventListener("cut", prevent);
    document.addEventListener("dragstart", prevent);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("keyup", onKeyUp);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    window.addEventListener("beforeprint", onBeforePrint);
    window.addEventListener("afterprint", onAfterPrint);

    const style = document.createElement("style");
    style.setAttribute("data-content-protection", "1");
    style.textContent =
      "@media print { body { display: none !important; } }" +
      "[data-protected] img { -webkit-user-drag: none; user-drag: none; -webkit-touch-callout: none; }";
    document.head.appendChild(style);

    return () => {
      document.removeEventListener("contextmenu", prevent);
      document.removeEventListener("copy", prevent);
      document.removeEventListener("cut", prevent);
      document.removeEventListener("dragstart", prevent);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("keyup", onKeyUp);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("beforeprint", onBeforePrint);
      window.removeEventListener("afterprint", onAfterPrint);
      style.remove();
    };
  }, []);

  return (
    <div data-protected className="select-none" style={{ WebkitUserSelect: "none", userSelect: "none" }}>
      {children}
      <div
        aria-hidden="true"
        className="fixed inset-0 z-40 pointer-events-none"
        style={{ backgroundImage: wmBg, backgroundRepeat: "repeat" }}
        data-testid="content-watermark"
      />
      {obscured && (
        <div
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white/80 backdrop-blur-2xl text-center px-6"
          data-testid="content-protection-overlay"
        >
          <ShieldAlert className="w-10 h-10 text-orange-500 mb-3" />
          <p className="text-sm font-semibold text-stone-800">Protected content hidden</p>
          <p className="text-xs text-stone-500 mt-1">Return to this tab to continue reading.</p>
        </div>
      )}
    </div>
  );
}
